'use client'

import { useState } from 'react'
import { useRouter } from 'next/navigation'
import Image from 'next/image'
import { TopBar, BottomNav } from '@/components/layout/navigation'
import { useCartStore } from '@/store/cart-store'
import { useOrderStore } from '@/store/order-store'
import { useUserStore } from '@/store/user-store'
import { formatPrice, getDistance } from '@/lib/utils'
import { ShoppingCart, Minus, Plus, Trash2, Tag, ChevronRight, Ticket } from 'lucide-react'
import { toast } from 'sonner'

export default function CartPage() {
  const router = useRouter()
  const { items, removeItem, updateQuantity, clearCart, getTotal, getItemCount } = useCartStore()
  const { user } = useUserStore()
  const { getAvailableCoupons, selectCoupon, selectedCouponId, getSelectedCoupon, restoreCoupon } = useOrderStore()

  const [showCouponPicker, setShowCouponPicker] = useState(false)

  const subtotal = getTotal()
  const availableCoupons = getAvailableCoupons(subtotal)
  const selectedCoupon = getSelectedCoupon()

  const distance = getDistance(user ? '' : '建设路口')
  const freight = subtotal >= 99 ? 0 : Math.round(distance <= 3 ? 8 : distance <= 5 ? 12 : 18)

  let discount = 0
  if (selectedCoupon) {
    if (selectedCoupon.type === '满减') discount = selectedCoupon.value
    else if (selectedCoupon.type === '折扣') discount = Math.round(subtotal * selectedCoupon.value / 100)
    else if (selectedCoupon.type === '免运费') discount = freight
  }

  const finalTotal = subtotal + freight - discount

  const handleSelectCoupon = (couponId: string) => {
    if (selectedCouponId) restoreCoupon(selectedCouponId)
    selectCoupon(couponId)
    setShowCouponPicker(false)
    toast.success('优惠券已选择')
  }

  const handleClearCoupon = () => {
    if (selectedCouponId) restoreCoupon(selectedCouponId)
    selectCoupon(null)
  }

  const handleCheckout = () => {
    if (items.length === 0) {
      toast.error('购物车是空的')
      return
    }
    router.push('/order/confirm')
  }

  if (items.length === 0) {
    return (
      <div className="min-h-screen pb-16 bg-gray-50">
        <TopBar />
        <div className="flex flex-col items-center justify-center pt-32">
          <ShoppingCart className="w-16 h-16 text-gray-200" />
          <p className="text-gray-400 mt-3 text-sm">购物车空空如也</p>
          <button onClick={() => router.push('/menu')}
            className="mt-4 px-6 py-2 bg-pink-500 text-white text-sm rounded-full hover:bg-pink-600"
          >去逛逛</button>
        </div>
        <BottomNav />
      </div>
    )
  }

  return (
    <div className="min-h-screen pb-36 bg-gray-50">
      <TopBar />

      {/* 购物车信息 */}
      <div className="px-4 py-2 bg-white border-b border-gray-100">
        <p className="text-xs text-gray-400">共 {getItemCount()} 件商品</p>
      </div>

      {/* 商品列表 */}
      <div className="px-4 mt-2 space-y-2">
        {items.map(item => (
          <div key={item.id} className="flex gap-3 p-3 bg-white rounded-xl">
            <div className="relative w-20 h-20 rounded-lg overflow-hidden flex-shrink-0">
              <Image src={item.image} alt={item.name} fill className="object-cover" />
              {item.isCustom && (
                <span className="absolute top-0 left-0 px-1 py-0.5 bg-amber-500 text-white text-[8px] font-bold rounded-br">定制</span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-gray-800 truncate">{item.name}</p>
              <div className="flex flex-wrap gap-1 mt-0.5">
                <span className="text-[10px] text-gray-400">{item.size}</span>
                <span className="text-[10px] text-gray-300">|</span>
                <span className="text-[10px] text-gray-400">{item.taste}</span>
                {item.sugar && <><span className="text-[10px] text-gray-300">|</span><span className="text-[10px] text-gray-400">{item.sugar}</span></>}
              </div>
              {item.customText && (
                <p className="text-[10px] text-pink-400 mt-0.5 truncate">「{item.customText}」</p>
              )}
              <div className="flex items-center justify-between mt-2">
                <span className="text-sm font-bold text-pink-500">{formatPrice(item.price)}</span>
                <div className="flex items-center gap-2">
                  <div className="flex items-center border border-gray-200 rounded-lg">
                    <button onClick={() => updateQuantity(item.id, item.quantity - 1)} className="w-7 h-7 flex items-center justify-center">
                      <Minus className="w-3 h-3 text-gray-500" />
                    </button>
                    <span className="w-6 text-center text-xs font-bold">{item.quantity}</span>
                    <button onClick={() => updateQuantity(item.id, item.quantity + 1)} className="w-7 h-7 flex items-center justify-center">
                      <Plus className="w-3 h-3 text-gray-500" />
                    </button>
                  </div>
                  <button onClick={() => removeItem(item.id)} className="p-1">
                    <Trash2 className="w-4 h-4 text-gray-300 hover:text-red-400" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* 满减提示 */}
      {subtotal < 99 && (
        <div className="mx-4 mt-3 p-2.5 bg-amber-50 rounded-xl text-xs text-amber-600">
          🚚 再买 {formatPrice(99 - subtotal)} 即可免运费，当前运费 {formatPrice(freight)}
        </div>
      )}

      {/* 优惠券 */}
      <div className="mx-4 mt-3 bg-white rounded-xl p-3">
        <button
          onClick={() => setShowCouponPicker(!showCouponPicker)}
          className="w-full flex items-center justify-between"
        >
          <div className="flex items-center gap-2">
            <Tag className="w-4 h-4 text-pink-500" />
            <span className="text-sm text-gray-700">优惠券</span>
          </div>
          <div className="flex items-center gap-1">
            {selectedCoupon ? (
              <span className="text-sm text-pink-500 font-bold">
                -{selectedCoupon.type === '满减' ? formatPrice(selectedCoupon.value) : `${selectedCoupon.value}折`}
              </span>
            ) : (
              <span className="text-xs text-gray-400">{availableCoupons.length > 0 ? `${availableCoupons.length}张可用` : '暂无可用'}</span>
            )}
            <ChevronRight className="w-4 h-4 text-gray-400" />
          </div>
        </button>
      </div>

      {/* 优惠券选择面板 */}
      {showCouponPicker && (
        <div className="mx-4 mt-2 bg-white rounded-xl p-3 space-y-2 border border-gray-100">
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm font-bold text-gray-800">可用优惠券</span>
            {selectedCoupon && (
              <button onClick={handleClearCoupon} className="text-xs text-gray-400">不使用优惠券</button>
            )}
          </div>
          {availableCoupons.length > 0 ? availableCoupons.map(coupon => (
            <button key={coupon.id} onClick={() => handleSelectCoupon(coupon.id)}
              className={`w-full flex items-center p-3 rounded-lg border-2 transition-all ${selectedCouponId === coupon.id ? 'border-pink-500 bg-pink-50' : 'border-gray-100'}`}
            >
              <div className="w-14 h-14 bg-gradient-to-br from-pink-500 to-rose-400 rounded-lg flex flex-col items-center justify-center text-white flex-shrink-0">
                {coupon.type === '满减' && <><span className="text-lg font-bold">¥{coupon.value}</span><span className="text-[8px]">满{coupon.minAmount}可用</span></>}
                {coupon.type === '折扣' && <><span className="text-lg font-bold">{coupon.value / 10}折</span><span className="text-[8px]">无门槛</span></>}
                {coupon.type === '免运费' && <><span className="text-sm font-bold">免运费</span><span className="text-[8px]">无门槛</span></>}
              </div>
              <div className="flex-1 ml-3 text-left">
                <p className="text-sm font-bold text-gray-800">{coupon.name}</p>
                <p className="text-[10px] text-gray-400 mt-0.5">有效期至 {coupon.endTime}</p>
              </div>
            </button>
          )) : (
            <p className="text-xs text-gray-400 text-center py-4">暂无可用优惠券</p>
          )}
        </div>
      )}

      {/* 价格明细 */}
      <div className="mx-4 mt-3 bg-white rounded-xl p-3 space-y-1.5">
        <div className="flex justify-between text-xs text-gray-500">
          <span>商品小计</span><span>{formatPrice(subtotal)}</span>
        </div>
        <div className="flex justify-between text-xs text-gray-500">
          <span>配送费</span>
          <span className={freight === 0 ? 'text-green-500' : ''}>{freight === 0 ? '免运费' : formatPrice(freight)}</span>
        </div>
        {discount > 0 && (
          <div className="flex justify-between text-xs text-pink-500">
            <span>优惠减免</span><span>-{formatPrice(discount)}</span>
          </div>
        )}
        <div className="border-t border-gray-100 pt-1.5 flex justify-between items-center">
          <span className="text-sm text-gray-700">合计</span>
          <span className="text-lg font-bold text-pink-500">{formatPrice(finalTotal)}</span>
        </div>
      </div>

      {/* 底部结算栏 */}
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-100 safe-area-bottom">
        <div className="max-w-lg mx-auto flex items-center h-14 px-4 gap-3">
          <button onClick={() => { clearCart(); toast.success('购物车已清空') }} className="text-xs text-gray-400">
            清空
          </button>
          <div className="flex-1 text-right">
            <span className="text-sm text-gray-400">应付 </span>
            <span className="text-xl font-bold text-pink-500">{formatPrice(finalTotal)}</span>
          </div>
          <button onClick={handleCheckout}
            className="px-8 h-10 bg-pink-500 text-white text-sm font-bold rounded-full hover:bg-pink-600 active:scale-95 transition-all"
          >
            去结算
          </button>
        </div>
      </div>
    </div>
  )
}
